"use client";

import { useEffect, useState } from "react";

/**
 * `false` during SSR **and** during the client's hydration render; `true` from
 * the first post-hydration render onward.
 *
 * ## Why this exists (React 418 on the job feed, 2026-06-17 → 2026-08-21)
 *
 * The obvious way to defer client-only state is `typeof window !== "undefined"`.
 * That is WRONG for this purpose: it is already `true` during the hydration
 * render, so it does not prevent a mismatch. A `useState(false)` initializer is
 * evaluated identically on the server and in the hydration render, and only the
 * `useEffect` (which never runs on the server, and runs *after* hydration
 * commits) flips it — which is the property we need.
 *
 * The concrete bug: `posthog-js/react`'s `useFeatureFlagEnabled` reads the flag
 * SYNCHRONOUSLY in a `useState` initializer, from `localStorage`-persisted
 * flags. On `/[locale]/jobs` the feed streams behind a `<Suspense>` boundary
 * (`src/app/[locale]/(public)/jobs/page.tsx`), so that boundary hydrates AFTER
 * the shell's effects — including `PostHogProvider`'s `ensurePostHogInitialized`
 * — have already run. For a returning visitor the flag is therefore already
 * `true` on the boundary's FIRST client render, while the server HTML (rendered
 * before any flag existed) contains nothing. React 19 sees a host-instance
 * mismatch, throws #418, discards the server HTML for the whole boundary and
 * re-renders it client-side: ~40 ranked feed cards, pagination, the sidebar
 * slot and the ItemList JSON-LD. Measured at ~10% of feed pageviews, and 50.4%
 * of sessions where a badge actually rendered (vs 4.1% without — 12.4x).
 *
 * Use this for ANY value that can differ between the server render and the
 * first client render inside a streamed boundary: feature flags, market/geo
 * hints, `localStorage`, `matchMedia`, `navigator`. Gate the *render*, not just
 * an effect.
 */
export function useHydrated(): boolean {
	const [hydrated, setHydrated] = useState(false);
	useEffect(() => setHydrated(true), []);
	return hydrated;
}
